import { Component } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NuveiCard } from '@app/@shared/models/nuvei.model';
import { NuveiService } from '@app/@shared/services/nuvei.service';
import { PayoutService } from '@app/@shared/services/payout.service';
import { NuveiBank, STPData, SpeiService } from '@app/@shared/services/spei.service';
import { TransactionService } from '@app/@shared/services/transaction.service';
import { WalletService } from '@app/@shared/services/wallet.service';
import { Credentials } from '@app/auth';
import { ModalController, LoadingController, AlertController, ToastController, NavController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';

import { Subscription } from 'rxjs';
import { BankComponent } from './bank/bank.component';
import { SearchPipe } from '@app/@shared/pipes/search.pipe';

declare const withdrawal: any;

@Component({
  selector: 'app-payout',
  templateUrl: './payout.component.html',
  styleUrl: './payout.component.scss',
})
export class PayoutComponent {
  balance = 0;
  isLoading = true;
  cards: NuveiCard[] = [];
  cardSelected = 'new';
  cardIcon = 'generic';
  payoutS?: PayoutService;
  credentials?: Credentials;
  walletS?: WalletService;
  nuveiS?: NuveiService;
  speiS?: SpeiService;
  transactionS?: TransactionService;
  amount = 100;

  subscriptions: Subscription[] = [];
  transactionId = '';
  private nuveiScript: HTMLScriptElement | null = null; // Referencia al script
  banks: NuveiBank[] = [];

  upos: STPData[] = [];
  upoForm?: FormGroup;
  payoutForm?: FormGroup;

  constructor(
    private modalCtrl: ModalController,
    private loadingCtrl: LoadingController,
    private translateS: TranslateService,
    private alertCtrl: AlertController,
    private toastCtrl: ToastController,
    private fb: FormBuilder
  ) {}

  ionViewDidEnter() {
    this.getData().then(() => {
      // obtener los upos del usuario
      this.banks = this.speiS?.getBanks() ?? [];
      this.createForm();
    });
  }

  createForm() {
    this.upoForm = this.fb.group({
      beneficiaryBank: ['', Validators.required],
      beneficiaryNumber: [
        '',
        [Validators.required, Validators.minLength(18), Validators.maxLength(18), Validators.pattern(/^\d{18}$/)],
      ], // Ejemplo de validador de número
      // beneficiaryType: ['40', Validators.required],

      // // billingAddress
      // country: ['MX', Validators.required], // Valor por defecto
    });

    this.payoutForm = this.fb.group({
      stp: ['', Validators.required],
    });
  }

  async onSubmit(isPayout: boolean) {
    if (!this.payoutS) return;
    if (!this.speiS) return;

    const loading = await this.loadingCtrl.create({
      message: this.translateS.instant('loading'),
    });

    await loading.present();
    try {
      if (isPayout) {
        if (!this.payoutForm || this.payoutForm.invalid) {
          //  console.log('payout form invalid');
          // const response = await this.payoutS.
          return;
        }

        // console.log('payout valid');
        const { stp } = this.payoutForm.value;
        const response = await this.payoutS.addPayout(stp, this.amount);
        // console.log(JSON.stringify(response, null, 2));
        if (response.transaction.id) {
          const alert = await this.alertCtrl.create({
            subHeader: 'Solicitud de retiro registrada exitosamente',
            buttons: ['Ok'],
          });

          await alert.present();

          this.dismiss('close');
        } else {
          const toast = await this.toastCtrl.create({
            message: 'Error al solicitar retiro',
            duration: 3000,
            buttons: [{ icon: 'close' }],
          });

          await toast.present();
        }
        return;
      }

      if (this.upoForm && this.upoForm.valid) {
        // console.log('Formulario válido:', this.upoForm.value);
        // Aquí puedes enviar los datos a tu servicio o API
        const { beneficiaryNumber, beneficiaryBank } = this.upoForm.value;
        const pipeSearch = new SearchPipe();
        const bank: NuveiBank = pipeSearch.transform(this.banks, beneficiaryBank)[0];

        const stp: Pick<STPData, 'apmData' | 'billingAddress' | 'bankName'> = {
          apmData: {
            beneficiaryBank,
            beneficiaryName: `${this.credentials?.customer.firstName} ${this.credentials?.customer.lastName}`,
            beneficiaryNumber,
            beneficiaryType: '40',
          },
          billingAddress: {
            email: `${this.credentials?.email}`,
            country: 'MX',
          },
          bankName: bank.Bank ?? '',
        };

        const response = await this.payoutS.saveUpo(stp);

        // console.log(JSON.stringify(response, null, 2));
        this.upos = await this.speiS.getSTPData(this.credentials!.id);

        let message = 'Ocurrió un error al guardar la cuenta, por favor intente de nuevo';
        if (response.saved) {
          document.getElementById('itemSelectUpo')?.click();
          message = `Cuenta CLABE ${beneficiaryNumber} agregada exitosamente`;
          this.upoForm.reset();
        }

        const toast = await this.toastCtrl.create({
          message,
          duration: 3000,
          buttons: [{ icon: 'close' }],
        });

        await toast.present();

        return;
      }

      //console.log('Formulario inválido:');
    } catch (error) {
      console.error(error);
    } finally {
      await loading.dismiss();
    }
  }

  // Helper para acceder fácilmente a los controles en el HTML
  get f() {
    return this.upoForm!.controls;
  }

  async getData() {
    this.isLoading = true;
    if (!this.walletS || !this.nuveiS || !this.speiS) {
      return;
    }

    await this.nuveiS.saveUser();

    const wallets = await this.walletS.getByCustomerId(this.credentials!.id);

    const walletsAward = wallets.filter((w) => w.walletName.includes('award'));

    if (walletsAward) {
      this.balance = walletsAward.reduce((a, b) => a + b.balance, 0);
    }

    this.upos = await this.speiS.getByCustomerId(this.credentials!.id);

    // console.log(this.upos);
    this.isLoading = false;
  }

  ionViewWillLeave() {
    this.subscriptions.forEach((s) => s.unsubscribe());
    this.subscriptions = [];
    // Limpiar el script y la instancia antes de cerrar el modal
    // this.cleanupNuveiResources();
  }

  // async getData() {
  //   this.isLoading = true;

  //   this.openCheckout();
  // }

  // async initRecharge() {
  //   if (!this.payoutS) {
  //     return;
  //   }
  //   if (!this.walletS) {
  //     return;
  //   }
  //   if (!this.credentials) {
  //     return;
  //   }

  //   // const loading = await this.loadingCtrl.create({
  //   //   message: this.translateS.instant('nuvei.loading.init'),
  //   // });

  //   // await loading.present();

  //   // const id = this.credentials.id;
  //   // const wallets = await this.walletS.getByCustomerId(id, 'award');

  //   const init = (await this.payoutS.initPayout({
  //     balance: this.balance,
  //     amount: this.amount,
  //   })) as PayoutInitResponse;

  //   console.log({ init });

  //   // await this.generalS.waitOneSecond();

  //   // const send = (await this.payoutS.sendUpo({
  //   //   transactionId: init.transaction.id,
  //   //   beneficiaryBank: '40014',
  //   //   beneficiaryNumber: '014560567550371290',
  //   //   beneficiaryType: '40',
  //   // })) as any;

  //   // console.log({ send });
  //   // await this.generalS.waitOneSecond();

  //   // const confirm = await this.payoutS.confirm({
  //   //   transactionId: init.transaction.id,
  //   //   amount: this.balance,
  //   //   userPaymentOptionId: send.upoCreated.userPaymentOptionId,
  //   // });

  //   // console.log({ confirm });

  //   // await loading.dismiss();

  //   // setTimeout(()=>data?.close(),1000);

  //   this.isLoading = false;
  //   return init;
  // }

  // private async openCheckout() {
  //   this.isLoading = true;
  //   if (!this.transactionS) return;
  //   const response = await this.initRecharge();

  //   console.log({ response });

  //   if (!response) return;
  //   this.transactionId = response.transaction.id;

  //   const sub = this.transactionS.subscribe(response.transaction.id).subscribe(async (t) => {
  //     if (this.walletS && t && t.status === 'completed') {
  //       const loading = await this.loadingCtrl.create({
  //         message: this.translateS.instant('nuvei.loading.confirmation'),
  //       });

  //       await loading.present();
  //       const { balance } = await this.walletS.getBalance();
  //       this.balance = balance;

  //       const currencyPipe = new CurrencyPipe('es-MX');

  //       const alert = await this.alertCtrl.create({
  //         header: 'Transferencia exitosa',
  //         subHeader: `La transferencia de  ${currencyPipe.transform(this.balance, 'MXN')} fue exitosa`,
  //         // message: this.translateS.instant(
  //         //   s && s.status === 'completed' ? 'nuvei.success.process' : 'nuvei.error.process'
  //         // ),
  //         buttons: ['Ok'],
  //       });
  //       this.transactionId = '';
  //       await loading.dismiss();
  //       await alert.present();
  //       this.dismiss('');
  //     }
  //   });

  //   this.subscriptions.push(sub);

  //   console.log(response);

  //   const role = await this.initializeNuveiWithdrawal(response.orderOpened);

  //   if (role) {
  //     if (role === 'approved') {
  //       this.transactionId = '';
  //       await this.dismiss('approved');
  //     } else if (role === 'pending') {
  //       const alert = await this.alertCtrl.create({
  //         header: 'Transacción pendiente',
  //         message: 'Una vez confirmado el pago por la entidad financiera se incrementará tu saldo automáticamente',
  //         buttons: [
  //           {
  //             text: 'Ok',
  //           },
  //         ],
  //       });

  //       await alert.present();
  //       this.transactionId = '';
  //       await this.dismiss('pending');
  //     } else if (role === 'expired') {
  //       await this.dismiss('expired');
  //     }
  //   } else {
  //     // await this.cancelRecharge();
  //   }
  // }

  // private async initializeNuveiWithdrawal(
  //   response: NuveiOpenOrderResponse
  // ): Promise<'approved' | 'pending' | 'expired' | null> {
  //   return new Promise((resolve) => {
  //     if (!this.credentials) return;
  //     if (!withdrawal) return;

  //     console.log(JSON.stringify(response, null, 2));

  //     const configWithdrawal = {
  //       renderTo: '#nuvei-withdrawal-container',
  //       sessionToken: response.sessionToken,
  //       merchantSiteId: response.merchantSiteId,
  //       merchantId: response.merchantId,
  //       userTokenId: btoa(this.credentials.id),
  //       userId: this.credentials.id,
  //       env: environment.production ? 'prod' : 'prod', // "prod" | 'int
  //       country: 'MX',
  //       locale: 'es', // de, es, fr, it, pl, pt, ru
  //       currency: 'MXN',
  //       amount: this.amount.toString(),
  //       pmWhitelist: ['apmgw_STPmex'],
  //       selectUpo: 'first', // "83648899", "first"
  //       selectPM: 'first', // first, none
  //       showResponseMessage: true, //false, {pending:false, decline:false, success:true, expired:false}
  //       logLevel: '6',
  //       email: this.credentials.email,
  //       billingAddress: {
  //         email: this.credentials.email,
  //         country: 'MX',
  //       },
  //       i18n: {
  //         pay_with: 'Pagar con ',
  //         cc_card: 'Tarjeta',
  //         apmgw_STPmex: 'SPEI',
  //       },
  //       openAmount:
  //         this.balance < 100
  //           ? undefined
  //           : {
  //               min: '100',
  //               max: this.balance.toString(),
  //             },
  //       onResult: async (result: any) => {
  //         console.log('onResult', result);

  //         if (result['result'] === 'APPROVED') {
  //           resolve('approved');
  //           // this.modalController.dismiss({ result, saved: true }, 'approved');
  //         } else if (result['result'] === 'PENDING') {
  //           // this.modalController.dismiss(null, 'pending');
  //           resolve('pending');
  //         } else if (result['session_expired']) {
  //           resolve('expired');
  //         } else {
  //           const toast = await this.toastCtrl.create({
  //             message: `${result['result']}: ${result['errorDescription'] ?? result['reason']}`,
  //             buttons: [{ icon: 'close' }],
  //             color: 'danger',
  //             duration: 3000,
  //           });

  //           await toast.present();

  //           resolve(null);
  //         }
  //       },
  //       onReady: (result: any) => {
  //         console.log('onReady', result);
  //       },
  //     };

  //     console.log(JSON.stringify(configWithdrawal, null, 2));

  //     // Configurar el checkout de Nuvei
  //     withdrawal(configWithdrawal);
  //   });
  // }

  // private cleanupNuveiResources() {
  //   // 1. Eliminar el script del DOM
  //   if (this.nuveiScript && document.body.contains(this.nuveiScript)) {
  //     this.renderer.removeChild(document.body, this.nuveiScript);
  //     this.nuveiScript = null;
  //   }

  //   console.log('Recursos de Nuvei limpiados.');
  // }

  async selectAllText(event: CustomEvent): Promise<void> {
    try {
      const ionInput = event.target; // Obtenemos el componente ion-input
      const nativeInput = await (ionInput as any).getInputElement(); // Obtenemos el input nativo
      nativeInput.select(); // Seleccionamos todo el texto
    } catch (error) {}
  }

  async dismiss(role: string) {
    this.modalCtrl.dismiss(null, role);
    // this.navCtrl.navigateBack(['/','wallet']);
  }

  // async accountCapture() {
  //   if (!this.nuveiS) return;

  //   const loading = await this.loadingCtrl.create({
  //     message: this.translateS.instant('loading'),
  //   });

  //   loading.present();

  //   const capture = await this.nuveiS.getAccountCapture();

  //   console.log(capture);

  //   const nombreVentana = '_blank'; // o un nombre personalizado
  //   const propiedades = 'width=800,height=600';

  //   loading.dismiss();

  //   window.open(capture?.redirectUrl, nombreVentana, propiedades);

  //   setTimeout(() => this.dismiss('close'));
  // }

  async openSearchBank() {
    const modal = await this.modalCtrl.create({
      component: BankComponent,
      componentProps: {
        banks: this.banks,
      },
    });

    await modal.present();

    const { data, role } = await modal.onWillDismiss();

    if (data && role === 'save') {
      this.upoForm?.get('beneficiaryBank')?.setValue(data.Code);
    }
  }

  changeAmount(ev: CustomEvent) {
    if (!isNaN(+ev.detail.value)) {
      this.amount = +ev.detail.value;
    } else {
      this.amount = 100;
      ev.detail.value = 100;
    }
  }

  async deleteUpo(stp: STPData) {
    const alert = await this.alertCtrl.create({
      header: `Eliminar cuenta?`,
      subHeader: `${stp.bankName} (${stp.apmData.beneficiaryNumber.slice(
        0,
        1
      )}****${stp.apmData.beneficiaryNumber.slice(-4)})`,
      message: 'Esta operación no se puede deshacer',
      buttons: [
        {
          text: 'Eliminar',
          handler: async () => {
            const loading = await this.loadingCtrl.create({
              message: 'Eliminando...',
            });

            await loading.present();

            try {
              const deleted = await this.payoutS?.deleteUpo(stp.id!);

              // console.log(JSON.stringify(deleted, null, 2));
              // if (deleted && deleted.deleted.deleted) {

              // }
              this.upos = (await this.speiS?.getSTPData(this.credentials!.id)) ?? [];
            } catch (error) {
            } finally {
              loading.dismiss();
            }
          },
        },
        'Cerrar',
      ],
    });

    alert.present();
  }
  get clabeCtrl() {
    return this.upoForm?.get('beneficiaryNumber');
  }

  clabeError(): string {
    const c = this.clabeCtrl;
    if (!c) return '';
    if (c.hasError('required')) return 'Ingresa tu CLABE.';
    if (c.hasError('minlength')) return 'La CLABE debe tener exactamente 18 dígitos.';
    if (c.hasError('maxlength')) return 'La CLABE no puede tener más de 18 dígitos.';
    if (c.hasError('pattern')) return 'La CLABE solo admite números (sin letras ni espacios).';
    return '';
  }

  onClabeInput(ev: CustomEvent) {
    const raw = (ev.detail as any)?.value ?? '';
    const onlyDigits = String(raw).replace(/\D+/g, ''); // quita letras y espacios
    const trimmed = onlyDigits.slice(0, 18); // máximo 18
    if (trimmed !== raw) {
      this.clabeCtrl?.setValue(trimmed, { emitEvent: false });
    }
    // si quieres marcar como tocado al modificar automáticamente:
    this.clabeCtrl?.markAsDirty();
  }
}
